// Top-level page: list of SubdomainMonitor configs with per-row badge,
// schedule picker, last-run timestamp, edit / delete / run-now actions.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Edit3, Globe, Pause, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { subdomainMonitorsAPI } from '../../services/api';
import { useSubdomainMonitorBadge } from '../../contexts/SubdomainMonitorBadgeContext';
import ConfirmModal from '../common/ConfirmModal';
import SubdomainMonitorModal from './SubdomainMonitorModal';
import { formatAbsoluteDate } from '../../utils/time';

const SCHEDULE_LABEL = {
  hourly: 'Hourly',
  every_6h: 'Every 6h',
  daily: 'Daily',
  weekly: 'Weekly',
};

function fmt(ts) {
  if (!ts) return 'Never';
  return formatAbsoluteDate(ts);
}

export default function SubdomainMonitorList() {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, monitor: null });
  const [busy, setBusy] = useState(false);
  const { refresh: refreshBadge } = useSubdomainMonitorBadge();

  const fetchAll = useCallback(async () => {
    try {
      const data = await subdomainMonitorsAPI.list();
      setMonitors(data.monitors || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = async () => {
    setModalOpen(false);
    setEditTarget(null);
    await fetchAll();
    refreshBadge();
  };

  const handleDelete = async () => {
    if (!confirmDelete.monitor) return;
    setBusy(true);
    try {
      await subdomainMonitorsAPI.remove(confirmDelete.monitor.id);
      setMonitors((prev) => prev.filter((m) => m.id !== confirmDelete.monitor.id));
      setConfirmDelete({ open: false, monitor: null });
      refreshBadge();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePause = async (m) => {
    const next = m.status === 'active' ? 'paused' : 'active';
    try {
      await subdomainMonitorsAPI.update(m.id, { status: next });
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const runNow = async (m) => {
    try {
      await subdomainMonitorsAPI.runNow(m.id);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-shinobi-green" />
            <h1 className="text-xl font-semibold text-slate-900">Subdomain Monitoring</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Recurring subdomain enumeration. New FQDNs since you last looked are flagged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditTarget(null); setModalOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-shinobi-green/25 text-sm"
        >
          <Plus className="w-4 h-4" />
          New monitor
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Loading monitors...</div>
      ) : monitors.length === 0 ? (
        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
          <Globe className="w-7 h-7 mx-auto mb-2 opacity-60" />
          No subdomain monitors yet. Create one to start watching a root domain.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
          <table className="w-full text-sm text-left text-slate-800">
            <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
              <tr>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Schedule</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last run</th>
                <th className="px-4 py-3">Next run</th>
                <th className="px-4 py-3 text-right">Findings</th>
                <th className="px-4 py-3 text-right">New</th>
                <th className="px-4 py-3 w-44 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {monitors.map((m) => (
                <tr
                  key={m.id}
                  className="border-t border-cyber-300/30 hover:bg-cyber-200/40 cursor-pointer"
                  onClick={(e) => {
                    // Don't navigate if a button inside the row was clicked.
                    if (e.target.closest('button')) return;
                    window.location.assign(`/subdomain-monitors/${m.id}`);
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-shinobi-green">{m.root_domain}</div>
                    {m.label && <div className="text-xs text-slate-500 mt-0.5">{m.label}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-sm">{SCHEDULE_LABEL[m.schedule_kind] || m.schedule_kind}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                      m.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                        : 'bg-slate-100 text-slate-700 border-slate-300'
                    }`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmt(m.last_run_at)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmt(m.next_run_at)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{m.finding_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {m.new_count > 0 ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gradient-to-r from-shinobi-green/30 to-cyan-400/30 text-shinobi-green border border-wasabi-300">
                        +{m.new_count}
                      </span>
                    ) : (
                      <span className="text-slate-500">{m.new_count}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => runNow(m)}
                        title="Run now"
                        className="p-1.5 rounded text-slate-500 hover:text-cyan-700 hover:bg-cyan-100"
                        disabled={m.status !== 'active'}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePause(m)}
                        title={m.status === 'active' ? 'Pause' : 'Resume'}
                        className="p-1.5 rounded text-slate-500 hover:text-amber-700 hover:bg-amber-100"
                      >
                        {m.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditTarget(m); setModalOpen(true); }}
                        title="Edit"
                        className="p-1.5 rounded text-slate-500 hover:text-shinobi-green hover:bg-wasabi-100"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete({ open: true, monitor: m })}
                        title="Delete"
                        className="p-1.5 rounded text-slate-500 hover:text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <Link
                        to={`/subdomain-monitors/${m.id}`}
                        className="p-1.5 rounded text-slate-500 hover:text-slate-800"
                        title="Open"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubdomainMonitorModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditTarget(null); }}
        onSaved={handleCreated}
        target={editTarget}
      />

      <ConfirmModal
        isOpen={confirmDelete.open}
        onClose={() => !busy && setConfirmDelete({ open: false, monitor: null })}
        onConfirm={handleDelete}
        title="Delete monitor"
        message={
          confirmDelete.monitor
            ? `Delete the monitor for ${confirmDelete.monitor.root_domain}? All discovered subdomains and run history will be removed.`
            : ''
        }
        confirmText={busy ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
